import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Server, Shield, Cloud, HardDrive, Cog, Radio, Brain, Users2, Network } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { playMouseClick } from "@/lib/sounds";

const iconMap = {
  "managed-it": Server, "networking": Network, "security": Shield, "cloud-servers": Cloud,
  "backup-dr": HardDrive, "automation": Cog, "broadcast": Radio, "consulting": Brain, "cloud-collab": Users2,
};

export default function Services({ services, whatsapp }) {
  const [open, setOpen] = useState(null);
  return (
    <section id="servicios" data-testid={HOME.services} className="relative py-24 sm:py-28 px-5 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeader eyebrow="Servicios" title="Lo que resolvemos para nuestros clientes" sub="Nueve líneas de servicio cubriendo todo el ciclo de vida de tu infraestructura tecnológica." />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line-2 border border-line-2">
          {services.map((s, i) => (
            <CaseCard key={s.id || s.slug} svc={s} idx={i}
              isOpen={open?.id === s.id}
              onOpen={() => setOpen(s)} />
          ))}
        </div>
      </div>
      <AnimatePresence>
        {open && <ServiceModal svc={open} onClose={() => setOpen(null)} whatsapp={whatsapp} />}
      </AnimatePresence>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, sub, align = "left" }) {
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <div className="eyebrow mb-4">{eyebrow}</div>
      <h2 className="text-fg font-semibold tracking-tight leading-[1.05]" style={{ fontSize: "clamp(32px, 4vw, 56px)" }}>{title}</h2>
      {sub && <p className="mt-5 text-fg-2 text-[17px] leading-relaxed max-w-2xl">{sub}</p>}
    </div>
  );
}

function CaseCard({ svc, idx, onOpen, isOpen }) {
  const Icon = iconMap[svc.slug] || Server;
  return (
    <motion.button type="button" data-testid={`service-card-${svc.slug}`}
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (idx % 3) * 0.06 }}
      animate={{ opacity: isOpen ? 0 : 1 }}
      onClick={() => { playMouseClick(); onOpen(); }}
      className="case-card text-left p-7 group flex flex-col h-full bg-surface"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="w-11 h-11 border border-line flex items-center justify-center bg-base">
          <Icon className="w-5 h-5 text-fg-2 group-hover:text-brand transition-colors" />
        </div>
        <span className="text-faint text-[11px] font-medium tracking-wider">{String(idx+1).padStart(2,"0")}/{String(9).padStart(2,"0")}</span>
      </div>
      <h3 className="text-fg text-[20px] font-semibold leading-tight">{svc.title}</h3>
      <p className="text-mute text-[14px] mt-3 leading-relaxed flex-1">{svc.short}</p>
      <div className="mt-6 pt-5 border-t border-line-2 flex items-center justify-between text-fg-2 text-[13px] font-medium">
        <span>Ver detalle</span>
        <ArrowUpRight className="arrow w-4 h-4 text-fg-2" />
      </div>
    </motion.button>
  );
}

function ServiceModal({ svc, onClose, whatsapp }) {
  const Icon = iconMap[svc.slug] || Server;
  const handleClose = () => { playClick(); onClose(); };
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ perspective: "1000px" }}
    >
      <motion.div className="absolute inset-0 bg-black/75" onClick={handleClose} />
      <motion.div
        initial={{ rotateY: 90, scale: 0.75, opacity: 0 }}
        animate={{ rotateY: 0, scale: 1, opacity: 1 }}
        exit={{ rotateY: -90, scale: 0.75, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative bg-surface border border-line max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-surface border-b border-line-2 px-7 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-line flex items-center justify-center bg-base">
              <Icon className="w-4 h-4 text-brand" />
            </div>
            <div>
              <div className="eyebrow eyebrow-mute mb-0.5">{svc.code}</div>
              <div className="text-fg font-semibold text-[15px] leading-none">{svc.title}</div>
            </div>
          </div>
          <button onClick={handleClose} className="w-9 h-9 border border-line text-fg hover:border-fg flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-7 py-7">
          <div className="eyebrow eyebrow-mute mb-2">Resumen</div>
          <p className="text-fg-2 text-[16px] leading-relaxed">{svc.short}</p>
          <div className="hairline my-7" />
          <div className="eyebrow eyebrow-mute mb-4">Qué incluímos</div>
          <ul className="space-y-3">
            {svc.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-fg-2">
                <span className="mt-2 w-1 h-1 bg-brand inline-block flex-shrink-0" />
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
          <div className="hairline my-7" />
          <div className="eyebrow eyebrow-mute mb-3">Próximo paso</div>
          <p className="text-mute text-[14px] leading-relaxed mb-5">Si te interesa este servicio o querés evaluar cómo aplica a tu organización, te coordinamos una primera charla sin compromiso.</p>
          <div className="flex flex-wrap gap-3">
            <a href={`https://wa.me/${whatsapp || "5491166861309"}?text=${encodeURIComponent("Hola LUVA, me interesa el servicio: " + svc.title)}`}
              target="_blank" rel="noreferrer"
              className="btn-solid inline-flex items-center gap-2 px-5 py-3 text-[13px] rounded-sm">
              Solicitar reunión <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <a href="#contacto" onClick={handleClose} className="btn-line inline-flex items-center gap-2 px-5 py-3 text-[13px] rounded-sm">
              Formulario de contacto
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
